import { type FormEvent, type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import type { Verstoss } from '../api/fehler';
import { Feld } from '../komponenten/Feld';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die drei Felder eines Kennwortwechsels: bisheriges Kennwort, neues,
 * Wiederholung (BEN-06).
 *
 * EIN BAUSTEIN FÜR ZWEI WEGE (AeA-48, Wunsch vom 05.10.2026). Der erzwungene
 * Wechsel nach der Anmeldung und der freiwillige in „Mein Konto“ fragen
 * dasselbe und melden dieselben Fehler. Zwei Formulare liefen beim ersten
 * neuen Fehlerfall auseinander. Was sich unterscheidet — wohin gesendet wird,
 * was danach geschieht, welche Knöpfe daneben stehen —, kommt von außen.
 *
 * Die Wiederholung des neuen Passworts wird im BROWSER geprüft und nicht
 * serverseitig — sie ist eine Tippfehlerhilfe, keine fachliche Regel. Der
 * Server sieht nur ein Passwort; ihm ein zweites zu schicken hieße, es ein
 * zweites Mal über die Leitung zu geben.
 */
const FELDER = ['altesPasswort', 'neuesPasswort', 'wiederholung'] as const;

export function Kennwortformular({
  senden,
  beiFertig,
  knopf,
  hilfeAltes,
  naechsterSchrittAltes,
  daneben,
}: {
  readonly senden: (angabe: { altesPasswort: string; neuesPasswort: string }) => Promise<void>;
  readonly beiFertig: () => void;
  /** Die Beschriftung des Absendeknopfs. */
  readonly knopf: string;
  readonly hilfeAltes: string;
  /** Was tun, wenn das bisherige Kennwort nicht stimmt. */
  readonly naechsterSchrittAltes: string;
  /** Weitere Knöpfe neben dem Absenden — Abmelden, Abbrechen. */
  readonly daneben?: ReactNode;
}): JSX.Element {
  const [altes, setzeAltes] = useState('');
  const [neues, setzeNeues] = useState('');
  const [wiederholung, setzeWiederholung] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [ungleich, setzeUngleich] = useState(false);

  const altesFeld = useRef<HTMLInputElement>(null);
  const neuesFeld = useRef<HTMLInputElement>(null);
  const wiederholungFeld = useRef<HTMLInputElement>(null);
  const meldungKasten = useRef<HTMLDivElement>(null);

  /**
   * Fokus NACH dem Rendern.
   *
   * Früher stand er unmittelbar in der Absendefunktion — dort hängen
   * aria-invalid und der Fehlertext noch nicht am Feld, weil React die
   * Zustandsänderung erst danach übernimmt. Ein Vorleseprogramm las dann ein
   * Feld vor, das noch nicht als fehlerhaft markiert war.
   */
  useEffect(() => {
    if (ungleich) wiederholungFeld.current?.focus();
  }, [ungleich]);

  useEffect(() => {
    if (fehler === undefined) return;
    if (fehler.kennung === 'anmeldung_fehlgeschlagen') {
      altesFeld.current?.focus();
      altesFeld.current?.select();
    } else if (fehler.alleZumFeld('neuesPasswort').length > 0 || fehler.kennung.startsWith('passwort')) {
      neuesFeld.current?.focus();
      neuesFeld.current?.select();
    } else {
      meldungKasten.current?.focus();
    }
  }, [fehler]);

  /**
   * Ein falsches bisheriges Kennwort ist ein Feldfehler und gehört AN das
   * Feld (ERR-03). Die API kann ihn hier nicht liefern — sie antwortet mit
   * derselben Kennung wie bei einer gescheiterten Anmeldung, und das ist dort
   * richtig so. Also wird er hier erzeugt.
   */
  const altesVerstoss: readonly Verstoss[] =
    fehler?.kennung === 'anmeldung_fehlgeschlagen'
      ? [
          {
            kennung: 'altes_kennwort_falsch',
            feld: 'altesPasswort',
            meldung: {
              wasGeschah: 'Das bisherige Kennwort stimmt nicht.',
              auswirkung: 'Das Passwort wurde nicht geändert.',
              naechsterSchritt: naechsterSchrittAltes,
            },
          },
        ]
      : (fehler?.alleZumFeld('altesPasswort') ?? []);

  async function absenden(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (laeuft) return;

    if (neues !== wiederholung) {
      setzeUngleich(true);
      return;
    }
    setzeUngleich(false);

    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      await senden({ altesPasswort: altes, neuesPasswort: neues });
      beiFertig();
    } catch (ursache) {
      // Das bisherige Kennwort wird NICHT geleert. Es stillschweigend zu
      // löschen ist ein Datenverlust ohne Ankündigung — ERR-03 verlangt das
      // Gegenteil: "bereits erfasste Eingaben bleiben vollständig erhalten".
      // Markiert wird es, damit ein Neutippen bequem bleibt.
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <>
      {fehler !== undefined && (
        <Meldung fehler={fehler} gezeigteFelder={FELDER} verweis={meldungKasten} />
      )}

      <form onSubmit={(e) => void absenden(e)} noValidate>
        <Feld
          beschriftung="Bisheriges Kennwort"
          art="password"
          wert={altes}
          aendern={setzeAltes}
          pflicht
          automatik="current-password"
          verweis={altesFeld}
          verstoesse={altesVerstoss}
          hilfe={hilfeAltes}
        />

        <Feld
          beschriftung="Neues Passwort"
          art="password"
          wert={neues}
          aendern={setzeNeues}
          pflicht
          automatik="new-password"
          verweis={neuesFeld}
          verstoesse={fehler?.alleZumFeld('neuesPasswort') ?? []}
          // Die Mindestlänge steht in sys.anmelderichtlinie und ist durch die
          // Administration änderbar (SYS-10). Sie hier als Zahl hinzuschreiben
          // hieße, sie an zwei Orten zu führen — und die Oberfläche hätte
          // beim nächsten Mal unrecht. Die genaue Zahl nennt die Meldung des
          // Servers, wenn sie unterschritten wird.
          hilfe="Eine Folge mehrerer Wörter ist leichter zu merken und schwerer zu raten als ein kurzes Passwort mit Sonderzeichen. Ist es zu kurz, nennt Ihnen das System die verlangte Mindestlänge."
        />

        <Feld
          beschriftung="Neues Passwort wiederholen"
          art="password"
          wert={wiederholung}
          aendern={(wert) => {
            setzeWiederholung(wert);
            if (ungleich) setzeUngleich(false);
          }}
          pflicht
          automatik="new-password"
          verweis={wiederholungFeld}
          verstoesse={
            ungleich
              ? [
                  {
                    kennung: 'wiederholung_abweichend',
                    feld: 'wiederholung',
                    meldung: {
                      wasGeschah: 'Die beiden Eingaben stimmen nicht überein.',
                      auswirkung: 'Das Passwort wurde nicht geändert.',
                      naechsterSchritt: 'Bitte geben Sie das neue Passwort noch einmal ein.',
                    },
                  },
                ]
              : (fehler?.alleZumFeld('wiederholung') ?? [])
          }
        />

        <div className="knopfreihe">
          <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
            {laeuft ? 'Wird gespeichert …' : knopf}
          </button>
          {daneben}
        </div>
      </form>
    </>
  );
}
